import cx from "classnames";
import React, { useMemo } from "react";

import CategoriesList, {
    OwnProps as CategoriesListOwnProps,
} from "@/mk2/apps/bazaar/components/CategoriesList";
import { Category } from "@/mk2/apps/bazaar/components/CategoryListItem";
import { FacetsData } from "@/mk2/apps/bazaar/components/FacetsFilter";
import { CATEGORY_SELECT_NO_CATEGORIES } from "@/mk/autogenerated/translation";
import { getCategory, getLabel, list } from "@/mk/bazaar/product/categories";

import styles from "./CategorySelect.mscss";

interface OwnProps {
    className?: string;
    parentCategory?: string;
    facets: FacetsData;
    selected: string;
    generateCategoryTo: CategoriesListOwnProps["generateCategoryTo"];
}

type Props = OwnProps;

const CategorySelect: React.FunctionComponent<Props> = ({
    className,
    parentCategory,
    facets,
    selected,
    generateCategoryTo,
}) => {
    const counts = useMemo(
        () =>
            facets.cats.reduce<Record<string, number>>((acc, cur) => {
                acc[cur[0]] = cur.length === 2 ? cur[1] : cur[2];
                return acc;
            }, {}),
        [facets],
    );

    const parent: Category = useMemo(() => getCategory(parentCategory), [parentCategory]);

    const categories = useMemo(() => {
        const cats: Category[] = parent ? parent.children : list();
        // Display only categories with count > 0
        return cats.filter((cat) => counts[cat.name] > 0);
    }, [parent, counts]);

    return (
        <div className={cx(styles.CategorySelect, className)}>
            {parent && (
                <div className={styles.CategorySelect__categories__headline}>
                    <span className={styles.CategorySelect__categories__headline__text}>
                        {getLabel(parent.name)}
                    </span>
                </div>
            )}

            {categories.length === 0 ? (
                <div className={styles.CategorySelect__categories__empty}>
                    {CATEGORY_SELECT_NO_CATEGORIES}
                </div>
            ) : (
                <CategoriesList
                    className={styles.CategorySelect__categories__list}
                    categories={categories}
                    showIcons={!parentCategory}
                    selected={selected}
                    generateCategoryTo={generateCategoryTo}
                    counts={counts}
                />
            )}
        </div>
    );
};

CategorySelect.displayName = "CategorySelect";

export default React.memo<Props>(CategorySelect);
